function CheckIcon({ compact }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full bg-[#53BD0C] ${
        compact ? 'h-4 w-4' : 'h-5 w-5'
      }`}
    >
      <svg
        className={`text-white ${compact ? 'h-2.5 w-2.5' : 'h-3 w-3'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

function PricingCard({ plan, onStartLearning, paying, isSubscribed, compact = false }) {
  const buttonHeight = compact ? 'h-10' : 'h-14';
  const hasDiscount = plan.hasDiscount && plan.originalPrice > plan.price;

  return (
    <article
      className={`group relative flex h-full min-h-0 w-full min-w-0 max-w-full flex-col rounded-2xl bg-white transition-all duration-300 ${
        compact ? 'p-2' : 'p-4'
      }`}
    >
      <div
        className={`relative flex h-full min-h-0 w-full min-w-0 max-w-full flex-col rounded-2xl transition-all duration-300 ${
          compact ? 'p-3' : 'p-4'
        } ${plan.cardClass} ${plan.hoverCardClass || ''}`}
      >
        {plan.badge && (
          <span
            className={`absolute left-1/2 top-0 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-b-2xl bg-[#9369CC] font-bold text-white ${
              compact ? 'px-3 py-1.5 text-xs' : 'gap-2 px-4 py-2 text-base'
            }`}
          >
            <svg
              className={`shrink-0 text-[#FFD54F] ${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            {plan.badge}
          </span>
        )}

        <div
          className={`flex shrink-0 flex-col overflow-hidden ${compact ? 'h-[128px] pt-7' : 'h-[240px] pt-11'}`}
        >
          <h3
            className={`line-clamp-2 shrink-0 text-center font-bold leading-tight text-[#1B1B1B] ${
              compact ? 'text-lg' : 'text-[32px]'
            }`}
          >
            {plan.name}
          </h3>

          <div className={`shrink-0 text-center ${compact ? 'mt-1' : 'mt-2'}`}>
            <p
              className={`font-medium leading-none text-[#999999] line-through transition-colors duration-300 ${
                compact ? 'h-4 text-sm' : 'h-7 text-lg'
              } ${hasDiscount ? '' : 'invisible'}`}
            >
              {hasDiscount ? plan.originalPriceFormatted || `₹${plan.originalPrice}` : '₹0'}
            </p>
            <p
              className={`leading-none text-[#1B1B1B] transition-colors duration-300 ${compact ? 'text-lg' : ''} ${
                plan.hoverPriceClass || ''
              }`}
            >
              {plan.priceFormatted ? (
                <span className={`font-bold tracking-tight ${compact ? 'text-[32px]' : 'text-[50px]'}`}>
                  {plan.priceFormatted}
                </span>
              ) : (
                <>
                  ₹
                  <span className={`font-bold tracking-tight ${compact ? 'text-[32px]' : 'text-[50px]'}`}>
                    {plan.price}
                  </span>
                </>
              )}
            </p>
            <p
              className={`font-semibold leading-tight text-[#1B1B1B] transition-colors duration-300 ${
                compact ? 'mt-6 text-sm' : 'mt-6 text-[24px]'
              } ${plan.hoverDurationClass || ''}`}
            >
              {plan.duration}
            </p>
          </div>
        </div>

        <hr
          className={`shrink-0 border-t transition-colors duration-300 ${
            plan.dividerClass || 'border-violet-200'
          } ${compact ? 'my-3' : 'my-5'}`}
        />

        <ul
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-color:#9369CC_#f3f4f6] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#9369CC]/40 [&::-webkit-scrollbar]:w-1.5 ${
            compact ? 'space-y-2 pr-1' : 'space-y-4 pr-1'
          }`}
        >
          {plan.features.map((feature) => (
            <li
              key={feature}
              className={`flex items-start font-medium text-[#1B1B1B] ${
                compact ? 'gap-2 text-xs' : 'gap-3 text-[20px]'
              }`}
            >
              <CheckIcon compact={compact} />
              <span className="min-w-0 flex-1 leading-snug">{feature}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => onStartLearning?.(plan)}
          disabled={paying || isSubscribed}
          className={`mt-4 w-full shrink-0 rounded-xl border-2 font-bold transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60 ${buttonHeight} ${
            compact ? 'text-sm' : 'text-[24px]'
          } ${plan.buttonClass} ${plan.hoverButtonClass || ''}`}
        >
          {paying ? 'Processing...' : isSubscribed ? 'Subscribed' : 'Start Learning'}
        </button>
      </div>
    </article>
  );
}

export default PricingCard;
